
import Vue from "vue";
import { Component } from "vue-property-decorator";
import IframeCanvasWrapper from "@/components/Canvas/IframeCanvasWrapper.vue";
import BuilderTopBar from "@/components/Builder/TopBar/BuilderTopBar.vue";
import { configStore, singularityStore, uiStore } from "@/store/store";
import { registerGlobalShortcuts } from "@/lib/keyboard-shortcuts/global-shortcuts";
import PreferencesModal from "@/components/Settings/Preferences/PreferencesModal.vue";
import KeyboardShortcutsHelpModal from "@/components/Help/KeyboardShortcutsHelpModal.vue";
import ElementStudio from "@/components/ElementStudio/ElementStudio.vue";
import SidebarPanels from "./SidebarPanels.vue";
import ConfirmDialog from "@/components/UI/ConfirmDialog.vue";
import AppLayout from "@/components/Reusable/AppLayout/AppLayout.vue";
import ClosingConfirmations from "@/components/Reusable/AppLayout/ClosingConfirmations.vue";
import TemplateDialog from "@/components/UI/TemplateDialog.vue";
import InvalidPostDialog from "@/components/UI/InvalidPostDialog.vue";
import { registerClipboardShortcuts } from "@/lib/copy-paste/copy-paste";
import { loadBuilderElementsFromWordPress } from "@/lib/wordpress/load";
import DesignLibraryModal from "@/components/Add/library/DesignLibraryModal.vue";
import FirstTimeVisit from "@/components/Onboarding/FirstTimeVisit.vue";
import ComponentRootTagWarning from "@/components/Notifications/ComponentRootTagWarning.vue";
import PreviewModeCloseButton from "@/components/Builder/TopBar/PreviewModeCloseButton.vue";

import SingularityLeftPanel from "@/components/Singularity/SingularityLeftPanel.vue";
import SingularityLeftPanelNew from "@/components/Singularity/SingularityLeftPanelNew.vue";
import SingularityDebugPanel from "@/components/Singularity/SingularityDebugPanel.vue";

import Singularity from "@/components/Singularity/Singularity.vue";

@Component({
  components: {
    ComponentRootTagWarning,
    PreviewModeCloseButton,
    FirstTimeVisit,
    DesignLibraryModal,
    InvalidPostDialog,
    TemplateDialog,
    ClosingConfirmations,
    IframeCanvasWrapper,
    BuilderTopBar,
    PreferencesModal,
    ElementStudio,
    KeyboardShortcutsHelpModal,
    SidebarPanels,
    ConfirmDialog,
    AppLayout,

    SingularityLeftPanel,

    SingularityDebugPanel,

    SingularityLeftPanelNew,
    Singularity
  }
})
export default class BuilderLayout extends Vue {
  mounted() {
    if (!uiStore.hasFullAccess) return;
    registerGlobalShortcuts().register();
  }

  beforeDestroy() {
    registerGlobalShortcuts().unbind();
    registerClipboardShortcuts().unbind();
  }

  get classList() {
    return {
      [`has-permission:${this.permission}`]: true,
      [`no-top-bar`]: this.isSingularityMode
    };
  }

  get isPreviewMode() {
    return uiStore.isPreviewMode;
  }

  get preferencesModalOpen() {
    return uiStore.panelState.preferencesModalOpen;
  }

  get elementStudioOpen() {
    return uiStore.panelState.elementStudioOpen;
  }

  get singularityUiState() {
    return singularityStore.singularityUiState;
  }

  get isSingularityMode() {
    return uiStore.isSingularityMode;
  }

  updatePreferencesModal(e: boolean) {
    uiStore.setPreferencesModalOpen(e);
  }

  get keyboardShortcutsHelpModalOpen() {
    return uiStore.panelState.keyboardShortcutsHelpModalOpen;
  }

  updateKeyboardShortcutsHelpModal(e: boolean) {
    uiStore.setKeyboardShortcutHelpModalOpen(e);
  }

  get activeElementId() {
    return uiStore.activeElementId;
  }

  get leftSidebarPanelState() {
    return uiStore.panelState.leftSidebarPanel;
  }
  get rightSidebarPanelState() {
    return uiStore.panelState.rightSidebarPanel;
  }

  get permission() {
    return configStore.permission?.slug;
  }

  showTemplateDialog = false;
  onIframeLoaded() {
    // @ts-ignore
    requestIdleCallback(() => {
      loadBuilderElementsFromWordPress();
    });

    if (this.isRenderedWithTemplate() && !this.containsTemplateContentArea) {
      this.showTemplateDialog = true;
    }

    registerClipboardShortcuts().register();
  }

  get showInvalidPostDialog() {
    return !!configStore.invalidPostData;
  }

  isRenderedWithTemplate() {
    return (
      window.Breakdance?.canvas?.document?.querySelector(
        "[data-rendered-with-template]"
      ) !== null
    );
  }

  getTemplateAttribute(attributeName: string) {
    if (!this.isRenderedWithTemplate()) {
      return null;
    }

    const templateElement = window.Breakdance?.canvas?.document?.querySelector(
      "[data-rendered-with-template]"
    );

    return templateElement?.getAttribute(attributeName);
  }

  get templateName() {
    return this.getTemplateAttribute("data-template-name") ?? "";
  }

  get templateId() {
    const templateIdString = this.getTemplateAttribute("data-template-id");

    return templateIdString ? parseInt(templateIdString) : null;
  }

  get isTemplateFallback() {
    return this.getTemplateAttribute("data-template-is-fallback") === "true";
  }

  get templatesPageUrl() {
    return this.getTemplateAttribute("data-template-page-url");
  }

  get containsTemplateContentArea() {
    return (
      window.Breakdance?.canvas?.document?.querySelector(
        ".bde-themeless-template-content-area"
      ) !== null
    );
  }

  get builderMode() {
    return configStore.builderMode;
  }

  get singularityModeStep1() {
    return (
      this.isSingularityMode &&
      this.singularityUiState.singularityRightPanel === "step1"
    );
  }
}
